import { useWallet } from '@solana/wallet-adapter-react'
import { Panel } from '~/components/ui'
import { byInstalledFirst, useAvailableWallets } from '~/features/wallet/hooks/useAvailableWallets'
import { useSelectWallet } from '~/features/wallet/hooks/useSelectWallet'

const shorten = (address: string) => `${address.slice(0, 4)}...${address.slice(-4)}`

/**
 * Connect a wallet from inside /trade.
 *
 * Listing needs a transaction signed and cancelling needs a message signed, both
 * by the wallet on the listing, and this page used to ask for one with no way to
 * give it. A wallet linked to the account is not a connected one: linking proves
 * ownership once, and this is the live session that can sign.
 *
 * `holder` is the wallet this action has to come from, when we know it. Being
 * connected to a different one is the same dead end as not being connected, so
 * it gets the same picker.
 *
 * Fractal is excluded for the reason the deposit picker gives: it has the signing
 * method but does not support signing a transaction we build.
 */
export const ConnectWallet = ({ holder, prompt }: { holder?: string | null; prompt: string }) => {
    const { disconnect } = useWallet()
    const { available } = useAvailableWallets({ require: 'transaction', exclude: ['Fractal'] })
    const { choose, pending, error, connecting, publicKey } = useSelectWallet()

    const address = publicKey?.toBase58()
    const wrongWallet = Boolean(address && holder && address !== holder)

    if (address && !wrongWallet) {
        return (
            <Panel className='flex flex-wrap items-center justify-between gap-2 p-3 text-xs text-white/70'>
                <span>Connected: {shorten(address)}</span>
                <button
                    onClick={() => disconnect().catch(() => undefined)}
                    className='min-h-[2rem] border border-white/20 px-3 uppercase tracking-widest hover:bg-white/10'
                >
                    Disconnect
                </button>
            </Panel>
        )
    }

    return (
        <Panel className='flex flex-col gap-3 p-3 text-sm text-white/70'>
            <p>
                {wrongWallet && holder
                    ? `You are connected to ${shorten(address as string)}. This needs the wallet ending in ${holder.slice(-4)}.`
                    : prompt}
            </p>

            {available.length === 0 ? (
                <p className='text-xs text-[#FFC53D]'>
                    No Solana wallet found in this browser. Install one such as Phantom, then reload this page.
                </p>
            ) : (
                <div className='flex flex-wrap gap-2'>
                    {[...available].sort(byInstalledFirst).map(({ adapter }) => (
                        <button
                            key={adapter.name}
                            disabled={connecting || pending !== undefined}
                            onClick={() => choose(adapter.name)}
                            className='flex min-h-[2.5rem] items-center gap-2 border border-[#03CEFF]/50 px-3 text-xs font-bold text-white hover:bg-[#03CEFF]/10 disabled:opacity-40'
                        >
                            {adapter.icon && <img src={adapter.icon} alt='' className='h-4 w-4' />}
                            {pending === adapter.name ? 'Connecting…' : adapter.name}
                        </button>
                    ))}
                </div>
            )}

            {/* Switching accounts inside one wallet happens in the wallet itself,
                so a mismatch also needs a way out of the current session. */}
            {wrongWallet && (
                <button
                    onClick={() => disconnect().catch(() => undefined)}
                    className='min-h-[2rem] self-start border border-white/20 px-3 text-xs uppercase tracking-widest hover:bg-white/10'
                >
                    Disconnect
                </button>
            )}

            {error && <p className='text-xs text-[#FF6B6B]'>{error}</p>}
        </Panel>
    )
}
